import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
    authLogin,
    authLogout,
    authMe,
    authRegister,
    authGoogleSession,
} from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const u = await authMe();
            setUser(u);
        } catch (_) {
            // Silent — anonymous visitors hit 401 by design
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // CRITICAL: if returning from Google OAuth callback, skip /me check —
        // AuthCallback handles the session exchange first.
        if (
            typeof window !== "undefined" &&
            window.location.hash?.includes("session_id=")
        ) {
            setLoading(false);
            return;
        }
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        const u = await authLogin(email, password);
        setUser(u);
        return u;
    };

    const register = async (payload) => {
        const u = await authRegister(payload);
        setUser(u);
        return u;
    };

    const logout = async () => {
        await authLogout();
        setUser(null);
    };

    const completeGoogle = async (session_id, role = "student") => {
        const u = await authGoogleSession(session_id, role);
        setUser(u);
        return u;
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                login,
                register,
                logout,
                completeGoogle,
                refresh,
                isAuthed: !!user,
                isTeacher: user?.role === "teacher",
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
